import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { httpDelete, httpGet, httpPost } from "../../services/httpService";
import {
  getScheduleEventsUrl,
  getScheduleEventsByPharmaciesUrl,
  getResourcesUrl,
  getAllPharmacistsUrl,
  addScheduleEventUrl,
  deleteScheduleEventsUrl,
  addBlockOutUrl,
  updateBlockOutUrl,
  deleteBlockOutUrl,
  getUsrAvailibilityUrl,
  getUsersAvailabilityByPriorityUrl,
  getScheduleEventsByCompanyUrl,
  saveGoogleEventsUrl,
  getGoogleEventsUrl,
  getAllUserWeekAvailabilityUrl,
  addAndUpdateTaskEventUrl,
  saveUserSettingsUrl,
  getUserDefaultCalendarViewUrl,
  getGoogleOAuthUrl,
  getGoogleCalendarStatusUrl,
  getGoogleCalendarsUrl,
  triggerGoogleCalendarSyncUrl,
  syncAppointmentToGoogleUrl,
  deleteAppointmentFromGoogleUrl,
  disconnectGoogleCalendarUrl,
} from "../../apiUrls/apiUrls";
import { logErrorWithPayload } from "../../services/logService";

const initialState = {
  isEventsLoading: false,
  isEventCalendarLoading: false,
  isSavingUserSettings: false,
  userDefaultCalendarView: null,
  isUserDefaultCalendarViewLoading: false,
  scheduleEvents: [],
  resources: [],
  userAvailibility: [],
  myAppointments: [],
  isMyAppointmentsLoading: false,
  googleSyncEvents: [],
  googleCalendarStatus: null,
  isGoogleCalendarStatusLoading: false,
  isGoogleCalendarSyncing: false,
  isGoogleCalendarConnecting: false,
  isGoogleCalendarDisconnecting: false,
  googleCalendarsList: [],
  googleCalendarSyncResult: null,
  showBookingSlots: false,
  bookingSlotsMode: "",
  setShowBacktoSlotsButton: false,
  scheduleEventsBySelectedPharmacies: [],
  isScheduleEventsByPharmaciesLoading: false,
  allUserWeekAvailability: [],
  isResourcesLoading: false,
  pharmacistResources: [],
  isConsultationRoute: false,
};

export const getScheduleEventsData = createAsyncThunk(
  "scheduleEvents/getScheduleEventsData",
  async (pharmacyId, thunkAPI) => {
    const urlToGet = getScheduleEventsUrl(pharmacyId);
    const respData = await httpGet(urlToGet);
    return respData;
  },
);
export const getResourcesData = createAsyncThunk(
  "scheduleEvents/getResourcesData",
  async ({ companyID, pharmacyId }) => {
    const urlToGet = getResourcesUrl(companyID, pharmacyId);
    const respData = await httpGet(urlToGet);
    return respData;
  },
);
export const getAllPharmacistsData = createAsyncThunk(
  "scheduleEvents/getAllPharmacistsData",
  async ({ companyID, pharmacyId }) => {
    const urlToGet = getAllPharmacistsUrl(companyID, pharmacyId);
    const respData = await httpGet(urlToGet);
    return respData;
  },
);
export const addScheduleEventData = createAsyncThunk(
  "scheduleEvents/addScheduleEventData",
  async (params, thunkAPI) => {
    const { data, pharmacyId } = params || {};
    // API schema rejects unknown props: send camelCase isNotify/roomId, no createdAt
    const { IsNotify, RoomId, createdAt, ...rest } = data || {};
    const isNotify = rest.isNotify ?? IsNotify;
    const roomId = rest.roomId ?? RoomId;
    const payload = {
      ...rest,
      isNotify: typeof isNotify === "boolean" ? isNotify : true,
      ...(roomId !== undefined && { roomId }),
    };
    const respData = await httpPost(addScheduleEventUrl(pharmacyId), payload);
    return respData;
  },
);
export const addBlockOutData = createAsyncThunk(
  "scheduleEvents/addBlockOutData",
  async (params, thunkAPI) => {
    const { data, pharmacyId } = params || {};
    const respData = await httpPost(addBlockOutUrl(pharmacyId), data);
    return respData;
  },
);
export const updateBlockOutData = createAsyncThunk(
  "scheduleEvents/updateBlockOutData",
  async (params, thunkAPI) => {
    const { data, pharmacyId } = params || {};
    const respData = await httpPost(updateBlockOutUrl(pharmacyId), data);
    return respData;
  },
);
export const deleteScheduleEventsData = createAsyncThunk(
  "scheduleEvents/deleteScheduleEventsData",
  async (params, thunkAPI) => {
    const { eventID, currentPharmacyId } = params;
    const urlToGet = deleteScheduleEventsUrl(eventID, currentPharmacyId);
    const respData = await httpDelete(urlToGet);
    return respData;
  },
);
export const deleteBlockOutData = createAsyncThunk(
  "scheduleEvents/deleteBlockOutData",
  async (params, thunkAPI) => {
    const { eventID, currentPharmacyId } = params;
    const urlToGet = deleteBlockOutUrl(eventID, currentPharmacyId);
    const respData = await httpDelete(urlToGet);
    return respData;
  },
);
export const getUsrAvailibilityData = createAsyncThunk(
  "scheduleEvents/getUsrAvailibilityData",
  async ({ userEmail, serviceID, pharmacyId }) => {
    const urlToGet = getUsrAvailibilityUrl(userEmail, serviceID, pharmacyId);
    const respData = await httpGet(urlToGet);
    return respData;
  },
);
export const getUsersAvailabilityByPriorityData = createAsyncThunk(
  "scheduleEvents/getUsersAvailabilityByPriorityData",
  async ({ companyID, serviceID, pharmacyID }) => {
    const urlToGet = getUsersAvailabilityByPriorityUrl(
      companyID,
      serviceID,
      pharmacyID,
    );
    const respData = await httpGet(urlToGet);
    return respData;
  },
);
export const getScheduleEventsByPharmaciesData = createAsyncThunk(
  "scheduleEvents/getScheduleEventsByPharmaciesData",
  async (arg, thunkAPI) => {
    const urlToGet = getScheduleEventsByPharmaciesUrl();
    // Accepts a plain id array, or { pharmacyIds, silent } (silent skips the loading flag).
    const pharmacyIds = Array.isArray(arg) ? arg : arg?.pharmacyIds;
    const payload = Array.isArray(pharmacyIds)
      ? pharmacyIds.filter((id) => Boolean(id))
      : [];
    const respData = await httpPost(urlToGet, payload);
    return respData;
  },
);
export const getMyAppointmentsData = createAsyncThunk(
  "scheduleEvents/getMyAppointmentsData",
  async () => {
    const urlToGet = getScheduleEventsByCompanyUrl();
    const respData = await httpGet(urlToGet);
    return respData;
  },
);
export const saveGoogleEventsData = createAsyncThunk(
  "scheduleEvents/saveGoogleEventsData",
  async (mappedEvents, thunkAPI) => {
    const urlToPost = saveGoogleEventsUrl();
    const respData = await httpPost(urlToPost, mappedEvents);
    return respData;
  },
);
export const getGoogleEventsData = createAsyncThunk(
  "scheduleEvents/getGoogleEventsData",
  async ({ companyID, pharmacyID }) => {
    const urlToGet = getGoogleEventsUrl(companyID, pharmacyID);
    const respData = await httpGet(urlToGet);
    return respData;
  },
);
export const getAllUserWeekAvailabilityData = createAsyncThunk(
  "scheduleEvents/getAllUserWeekAvailabilityData",
  async () => {
    const urlToGet = getAllUserWeekAvailabilityUrl();
    const respData = await httpGet(urlToGet);
    return respData;
  },
);
export const addAndUpdateTaskEventData = createAsyncThunk(
  "scheduleEvents/addAndUpdateTaskEventData",
  async (params, thunkAPI) => {
    const { data, pharmacyId } = params || {};
    const respData = await httpPost(addAndUpdateTaskEventUrl(pharmacyId), data);
    return respData;
  },
);
export const saveUserSettingsData = createAsyncThunk(
  "scheduleEvents/saveUserSettingsData",
  async (payload, thunkAPI) => {
    const urlToPost = saveUserSettingsUrl();
    const respData = await httpPost(urlToPost, payload);
    return respData;
  },
);
export const getUserDefaultCalendarViewData = createAsyncThunk(
  "scheduleEvents/getUserDefaultCalendarViewData",
  async (companyId, thunkAPI) => {
    const urlToGet = getUserDefaultCalendarViewUrl(companyId);
    const respData = await httpGet(urlToGet);
    return respData;
  },
);
// #region Google Calendar Async Thunks
export const getGoogleOAuthUrlData = createAsyncThunk(
  "scheduleEvents/getGoogleOAuthUrlData",
  async ({ pharmacyId = null, companyId = null } = {}) => {
    const urlToGet = getGoogleOAuthUrl(pharmacyId, companyId);
    const respData = await httpGet(urlToGet);
    return respData;
  },
);
export const getGoogleCalendarStatusData = createAsyncThunk(
  "scheduleEvents/getGoogleCalendarStatusData",
  async () => {
    const urlToGet = getGoogleCalendarStatusUrl();
    const respData = await httpGet(urlToGet);
    return respData;
  },
);
export const getGoogleCalendarsData = createAsyncThunk(
  "scheduleEvents/getGoogleCalendarsData",
  async () => {
    const urlToGet = getGoogleCalendarsUrl();
    const respData = await httpGet(urlToGet);
    return respData;
  },
);
export const triggerGoogleCalendarSyncData = createAsyncThunk(
  "scheduleEvents/triggerGoogleCalendarSyncData",
  async ({
    fullSync = false,
    pharmacyId = null,
    syncGoogleToTitanverse = true,
    companyId = null,
  } = {}) => {
    const urlToGet = triggerGoogleCalendarSyncUrl(
      fullSync,
      pharmacyId,
      syncGoogleToTitanverse,
      companyId,
    );
    const respData = await httpGet(urlToGet);
    return respData;
  },
);
export const syncAppointmentToGoogleData = createAsyncThunk(
  "scheduleEvents/syncAppointmentToGoogleData",
  async (appointmentId) => {
    const urlToPost = syncAppointmentToGoogleUrl(appointmentId);
    const respData = await httpPost(urlToPost, {});
    return respData;
  },
);
export const deleteAppointmentFromGoogleData = createAsyncThunk(
  "scheduleEvents/deleteAppointmentFromGoogleData",
  async (appointmentId) => {
    const urlToDelete = deleteAppointmentFromGoogleUrl(appointmentId);
    const respData = await httpDelete(urlToDelete);
    return respData;
  },
);
export const disconnectGoogleCalendarData = createAsyncThunk(
  "scheduleEvents/disconnectGoogleCalendarData",
  async () => {
    const urlToGet = disconnectGoogleCalendarUrl();
    const respData = await httpGet(urlToGet);
    return respData;
  },
);
// #endregion

const scheduleEventSlice = createSlice({
  name: "scheduleEvent",
  initialState,
  reducers: {
    setIsEventsLoading: (state, action) => {
      state.isEventsLoading = action?.payload || false;
    },
    setIsEventCalendarLoading: (state, action) => {
      state.isEventCalendarLoading = action?.payload || false;
    },
    setIsSavingUserSettings: (state, action) => {
      state.isSavingUserSettings = action?.payload || false;
    },
    setUserDefaultCalendarView: (state, action) => {
      state.userDefaultCalendarView = action?.payload || null;
    },
    setScheduleEventsList: (state, action) => {
      state.scheduleEvents = Array.isArray(action?.payload)
        ? action.payload
        : [];
      // ensure we never show skeleton due to this update
      state.isEventsLoading = false;
    },
    setMyAppointmentList: (state, action) => {
      state.myAppointments = Array.isArray(action?.payload)
        ? action.payload
        : [];
      // ensure we never show skeleton due to this update
      state.isMyAppointmentsLoading = false;
    },
    setShowBookingSlotsData: (state, action) => {
      state.showBookingSlots = action?.payload || false;
    },
    setBookingSlotsModeData: (state, action) => {
      state.bookingSlotsMode = action?.payload || "";
    },
    setShowBacktoSlotsButtonData: (state, action) => {
      state.showBacktoSlotsButton = action?.payload || false;
    },
    setMyAppointments: (state, action) => {
      state.myAppointments = Array.isArray(action?.payload)
        ? action.payload
        : [];
    },
    setGoogleSyncEvents: (state, action) => {
      state.googleSyncEvents = Array.isArray(action?.payload)
        ? action.payload
        : [];
    },
    setGoogleCalendarStatus: (state, action) => {
      state.googleCalendarStatus = action?.payload || null;
    },
    setIsGoogleCalendarSyncing: (state, action) => {
      state.isGoogleCalendarSyncing = action?.payload || false;
    },
    setGoogleCalendarsList: (state, action) => {
      state.googleCalendarsList = Array.isArray(action?.payload)
        ? action.payload
        : [];
    },
    setIsConsultationRouteData: (state, action) => {
      state.isConsultationRoute = action?.payload || false;
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(getScheduleEventsData.pending, (state) => {
        state.isEventsLoading = true;
        state.scheduleEvents = [];
      })
      .addCase(getScheduleEventsData.fulfilled, (state, action) => {
        state.isEventsLoading = false;
        state.scheduleEvents = action?.payload;
      })
      .addCase(getScheduleEventsData.rejected, (state, action) => {
        logErrorWithPayload(action?.error);
        state.isEventsLoading = false;
        state.scheduleEvents = [];
      })
      .addCase(getResourcesData.pending, (state) => {
        state.isEventsLoading = true;
        state.isResourcesLoading = true;
        state.resources = [];
      })
      .addCase(getResourcesData.fulfilled, (state, action) => {
        state.isEventsLoading = false;
        state.isResourcesLoading = false;
        state.resources = action?.payload;
      })
      .addCase(getResourcesData.rejected, (state, action) => {
        logErrorWithPayload(action?.error);
        state.isEventsLoading = false;
        state.isResourcesLoading = false;
        state.resources = [];
      })
      .addCase(getAllPharmacistsData.pending, (state) => {
        state.isEventsLoading = true;
        state.isResourcesLoading = true;
        state.pharmacistResources = [];
      })
      .addCase(getAllPharmacistsData.fulfilled, (state, action) => {
        state.isEventsLoading = false;
        state.isResourcesLoading = false;
        state.pharmacistResources = action?.payload;
      })
      .addCase(getAllPharmacistsData.rejected, (state, action) => {
        logErrorWithPayload(action?.error);
        state.isEventsLoading = false;
        state.isResourcesLoading = false;
        state.pharmacistResources = [];
      })
      .addCase(addScheduleEventData.pending, (state) => {
        state.isEventCalendarLoading = true;
      })
      .addCase(addScheduleEventData.fulfilled, (state, action) => {
        state.isEventCalendarLoading = false;
      })
      .addCase(addScheduleEventData.rejected, (state, action) => {
        logErrorWithPayload(action?.error);
        state.isEventCalendarLoading = false;
      })
      .addCase(deleteScheduleEventsData.pending, (state) => {
        state.isEventCalendarLoading = true;
      })
      .addCase(deleteScheduleEventsData.fulfilled, (state, action) => {
        state.isEventCalendarLoading = false;
      })
      .addCase(deleteScheduleEventsData.rejected, (state, action) => {
        logErrorWithPayload(action?.error);
        state.isEventCalendarLoading = false;
      })
      .addCase(addBlockOutData.pending, (state) => {
        state.isEventCalendarLoading = true;
      })
      .addCase(addBlockOutData.fulfilled, (state, action) => {
        state.isEventCalendarLoading = false;
      })
      .addCase(addBlockOutData.rejected, (state, action) => {
        logErrorWithPayload(action?.error);
        state.isEventCalendarLoading = false;
      })
      .addCase(deleteBlockOutData.pending, (state) => {
        state.isEventCalendarLoading = true;
      })
      .addCase(deleteBlockOutData.fulfilled, (state, action) => {
        state.isEventCalendarLoading = false;
      })
      .addCase(deleteBlockOutData.rejected, (state, action) => {
        logErrorWithPayload(action?.error);
        state.isEventCalendarLoading = false;
      })
      .addCase(updateBlockOutData.pending, (state) => {
        state.isEventCalendarLoading = true;
      })
      .addCase(updateBlockOutData.fulfilled, (state, action) => {
        state.isEventCalendarLoading = false;
      })
      .addCase(updateBlockOutData.rejected, (state, action) => {
        logErrorWithPayload(action?.error);
        state.isEventCalendarLoading = false;
      })
      .addCase(getUsrAvailibilityData.pending, (state) => {
        state.userAvailibility = [];
      })
      .addCase(getUsrAvailibilityData.fulfilled, (state, action) => {
        state.userAvailibility = action?.payload;
      })
      .addCase(getUsrAvailibilityData.rejected, (state, action) => {
        logErrorWithPayload(action?.error);
        state.userAvailibility = [];
      })
      .addCase(getUsersAvailabilityByPriorityData.pending, (state) => {
        state.isEventsLoading = true;
        state.userAvailibility = [];
      })
      .addCase(
        getUsersAvailabilityByPriorityData.fulfilled,
        (state, action) => {
          state.isEventsLoading = false;
          state.userAvailibility = action?.payload;
        },
      )
      .addCase(getUsersAvailabilityByPriorityData.rejected, (state, action) => {
        logErrorWithPayload(action?.error);
        state.isEventsLoading = false;
        state.userAvailibility = [];
      })
      .addCase(getScheduleEventsByPharmaciesData.pending, (state, action) => {
        if (action?.meta?.arg?.silent) return;
        state.isScheduleEventsByPharmaciesLoading = true;
        state.scheduleEventsBySelectedPharmacies = [];
      })
      .addCase(getScheduleEventsByPharmaciesData.fulfilled, (state, action) => {
        state.isScheduleEventsByPharmaciesLoading = false;
        state.scheduleEventsBySelectedPharmacies = action?.payload;
      })
      .addCase(getScheduleEventsByPharmaciesData.rejected, (state, action) => {
        logErrorWithPayload(action?.error);
        state.isScheduleEventsByPharmaciesLoading = false;
        state.scheduleEventsBySelectedPharmacies = [];
      })
      .addCase(getMyAppointmentsData.pending, (state) => {
        state.isMyAppointmentsLoading = true;
        state.myAppointments = [];
      })
      .addCase(getMyAppointmentsData.fulfilled, (state, action) => {
        state.isMyAppointmentsLoading = false;
        state.myAppointments = action?.payload;
      })
      .addCase(getMyAppointmentsData.rejected, (state, action) => {
        logErrorWithPayload(action?.error);
        state.isMyAppointmentsLoading = false;
        state.myAppointments = [];
      })
      .addCase(saveGoogleEventsData.pending, (state) => {
        state.isEventsLoading = true;
      })
      .addCase(saveGoogleEventsData.fulfilled, (state, action) => {
        state.isEventsLoading = false;
      })
      .addCase(saveGoogleEventsData.rejected, (state, action) => {
        logErrorWithPayload(action?.error);
        state.isEventsLoading = false;
      })
      .addCase(getGoogleEventsData.pending, (state) => {
        state.isEventsLoading = true;
      })
      .addCase(getGoogleEventsData.fulfilled, (state, action) => {
        state.isEventsLoading = false;
        state.googleSyncEvents = Array.isArray(action?.payload)
          ? action.payload
          : [];
      })
      .addCase(getGoogleEventsData.rejected, (state, action) => {
        logErrorWithPayload(action?.error);
        state.isEventsLoading = false;
      })
      .addCase(getAllUserWeekAvailabilityData.pending, (state) => {
        state.allUserWeekAvailability = [];
      })
      .addCase(getAllUserWeekAvailabilityData.fulfilled, (state, action) => {
        const payloadData =
          action?.payload && action?.payload?.result
            ? action.payload.result
            : action?.payload;
        state.allUserWeekAvailability = Array.isArray(payloadData)
          ? payloadData
          : [];
      })
      .addCase(getAllUserWeekAvailabilityData.rejected, (state, action) => {
        logErrorWithPayload(action?.error);
        state.allUserWeekAvailability = [];
      })
      .addCase(addAndUpdateTaskEventData.pending, (state) => {
        state.isEventCalendarLoading = true;
      })
      .addCase(addAndUpdateTaskEventData.fulfilled, (state, action) => {
        state.isEventCalendarLoading = false;
      })
      .addCase(addAndUpdateTaskEventData.rejected, (state, action) => {
        logErrorWithPayload(action?.error);
        state.isEventCalendarLoading = false;
      })
      .addCase(saveUserSettingsData.pending, (state) => {
        state.isSavingUserSettings = true;
      })
      .addCase(saveUserSettingsData.fulfilled, (state, action) => {
        state.isSavingUserSettings = false;
        if (action?.meta?.arg) {
          state.userDefaultCalendarView = {
            defaultCalendarView: action.meta.arg.defaultCalendarView,
            calendarFilters: action.meta.arg.calendarFilters,
          };
        }
      })
      .addCase(saveUserSettingsData.rejected, (state, action) => {
        logErrorWithPayload(action?.error);
        state.isSavingUserSettings = false;
      })
      .addCase(getUserDefaultCalendarViewData.pending, (state) => {
        state.isUserDefaultCalendarViewLoading = true;
      })
      .addCase(getUserDefaultCalendarViewData.fulfilled, (state, action) => {
        state.isUserDefaultCalendarViewLoading = false;
        const payloadData =
          action?.payload && action?.payload?.result !== undefined
            ? action.payload.result
            : action?.payload;
        state.userDefaultCalendarView = payloadData || null;
      })
      .addCase(getUserDefaultCalendarViewData.rejected, (state, action) => {
        logErrorWithPayload(action?.error);
        state.isUserDefaultCalendarViewLoading = false;
        state.userDefaultCalendarView = null;
      })
      // #region Google Calendar Reducers
      .addCase(getGoogleOAuthUrlData.pending, (state) => {
        state.isGoogleCalendarConnecting = true;
      })
      .addCase(getGoogleOAuthUrlData.fulfilled, (state) => {
        state.isGoogleCalendarConnecting = false;
      })
      .addCase(getGoogleOAuthUrlData.rejected, (state, action) => {
        logErrorWithPayload(action?.error);
        state.isGoogleCalendarConnecting = false;
      })
      .addCase(getGoogleCalendarStatusData.pending, (state) => {
        state.isGoogleCalendarStatusLoading = true;
      })
      .addCase(getGoogleCalendarStatusData.fulfilled, (state, action) => {
        state.isGoogleCalendarStatusLoading = false;
        const payloadData =
          action?.payload && action?.payload?.result !== undefined
            ? action.payload.result
            : action?.payload;
        state.googleCalendarStatus = payloadData || null;
      })
      .addCase(getGoogleCalendarStatusData.rejected, (state, action) => {
        logErrorWithPayload(action?.error);
        state.isGoogleCalendarStatusLoading = false;
      })
      .addCase(getGoogleCalendarsData.pending, (state) => {
        state.googleCalendarsList = [];
      })
      .addCase(getGoogleCalendarsData.fulfilled, (state, action) => {
        const payloadData =
          action?.payload && action?.payload?.result !== undefined
            ? action.payload.result
            : action?.payload;
        state.googleCalendarsList = Array.isArray(payloadData)
          ? payloadData
          : [];
      })
      .addCase(getGoogleCalendarsData.rejected, (state, action) => {
        logErrorWithPayload(action?.error);
        state.googleCalendarsList = [];
      })
      .addCase(triggerGoogleCalendarSyncData.pending, (state) => {
        state.isGoogleCalendarSyncing = true;
      })
      .addCase(triggerGoogleCalendarSyncData.fulfilled, (state, action) => {
        // This only means the sync was successfully kicked off on the server, not that it
        // finished - it now runs in the background. Actual completion (and the real
        // lastSyncedAt) comes from polling GET /status, so don't stamp it here.
        state.isGoogleCalendarSyncing = false;
        const payloadData =
          action?.payload && action?.payload?.result !== undefined
            ? action.payload.result
            : action?.payload;
        state.googleCalendarSyncResult = payloadData || null;
        if (state.googleCalendarStatus) {
          state.googleCalendarStatus = {
            ...state.googleCalendarStatus,
            isSyncing: true,
          };
        }
      })
      .addCase(triggerGoogleCalendarSyncData.rejected, (state, action) => {
        logErrorWithPayload(action?.error);
        state.isGoogleCalendarSyncing = false;
      })
      .addCase(disconnectGoogleCalendarData.pending, (state) => {
        state.isGoogleCalendarDisconnecting = true;
      })
      .addCase(disconnectGoogleCalendarData.fulfilled, (state) => {
        state.isGoogleCalendarDisconnecting = false;
        state.googleCalendarStatus = { isConnected: false };
        state.googleCalendarsList = [];
      })
      .addCase(disconnectGoogleCalendarData.rejected, (state, action) => {
        logErrorWithPayload(action?.error);
        state.isGoogleCalendarDisconnecting = false;
      });
    // #endregion
  },
});
export const {
  setIsEventsLoading,
  setIsEventCalendarLoading,
  setIsSavingUserSettings,
  setUserDefaultCalendarView,
  setScheduleEventsList,
  setMyAppointmentList,
  setShowBookingSlotsData,
  setBookingSlotsModeData,
  setShowBacktoSlotsButtonData,
  setMyAppointments,
  setGoogleSyncEvents,
  setGoogleCalendarStatus,
  setIsGoogleCalendarSyncing,
  setGoogleCalendarsList,
  setIsConsultationRouteData,
} = scheduleEventSlice.actions;
export default scheduleEventSlice.reducer;
